import { useMemo } from 'react';
import { useLeague } from '@/hooks/useLeague';
import { useLeagueMatches } from '@/hooks/useLeagueMatches';
import { useLeagueStandings } from '@/hooks/useLeagueStandings';
import { buildKnockoutBracket } from '@/utils/knockoutBracket';

/** Every tab a league page can have, in tab bar and swipe order. */
const LEAGUE_TABS = ['matches', 'standings', 'playoff', 'tournaments'] as const;

export type LeagueTab = (typeof LEAGUE_TABS)[number];

export const leagueTabPath = (leagueId: string, tab: LeagueTab): string =>
  tab === 'matches' ? `/matches/${leagueId}` : `/matches/${leagueId}/${tab}`;

/**
 * The tabs of one league page - the single list behind the tab bar, the swipe
 * order and the fallback of an absent tab's address. "Table" exists when
 * standings are stored, "Play-off" when the bracket model returns a stored
 * round; both stay absent until every answer is in, so the list changes once.
 *
 * Reads the queries the tabs themselves use, so it adds no request.
 *
 * @returns `isSettled` - false while an answer is still loading
 */
export function useLeagueTabs(leagueId: string): { tabs: LeagueTab[]; isSettled: boolean } {
  const { data: league } = useLeague(leagueId);
  const { data: matchesData, isFetched: isMatchesFetched } = useLeagueMatches(leagueId);
  const standings = useLeagueStandings(leagueId);

  // `isFetched` and not `isLoading`: a failed request is fetched again when a tab mounts, and the
  // list must not go back to unsettled then. Placeholder data is the table of the league shown before.
  const isSettled = !!league && isMatchesFetched && standings.isFetched && !standings.isPlaceholderData;
  // Fetched without data is a failed request - the answer is unknown: the tab stays and shows the error
  // ("Play-off" below follows the same rule for the matches request).
  const hasTable = isSettled && (!standings.data || standings.data.groups.length > 0);

  const matches = matchesData?.data;
  const hasPlayoff = useMemo(
    // A stored round only: a template alone shows no bracket. Ranks order the ties, never add a round.
    () =>
      isSettled && !!league &&
      (!matches || buildKnockoutBracket(matches, league.externalId, league.season, new Map()).some(r => r.isStored)),
    [isSettled, league, matches],
  );

  const tabs = useMemo(
    () => LEAGUE_TABS.filter(tab => (tab === 'standings' ? hasTable : tab === 'playoff' ? hasPlayoff : true)),
    [hasTable, hasPlayoff],
  );

  return { tabs, isSettled };
}
